"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Activity, Target, Zap, Sparkles, ChevronDown, ChevronUp } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import type { MetricInsight } from "@/lib/ai/utils"

interface TldrSummaryProps {
  metrics: MetricInsight[]
  fullAnalysis: string
}

// Map metric types to icons with subtle color variations
const getMetricIcon = (type: MetricInsight['type']) => {
  switch (type) {
    case 'volatility':
      return <Activity className="h-5 w-5 text-orange-600/90" />
    case 'average':
      return <Target className="h-5 w-5 text-blue-600/90" />
    case 'peak':
      return <Zap className="h-5 w-5 text-amber-600/90" />
    case 'summary':
      return <Sparkles className="h-5 w-5 text-primary" />
    default:
      return <Sparkles className="h-5 w-5 text-primary" />
  }
}

// Get subtle background color for each metric type
const getMetricBgColor = (type: MetricInsight['type']) => {
  switch (type) {
    case 'volatility':
      return 'bg-orange-50/50 dark:bg-orange-950/20 border-orange-200/30 dark:border-orange-800/30'
    case 'average':
      return 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200/30 dark:border-blue-800/30'
    case 'peak':
      return 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200/30 dark:border-amber-800/30'
    case 'summary':
      return 'bg-primary/5 border-primary/10'
    default:
      return 'bg-primary/5 border-primary/10'
  }
}

export function TldrSummary({ metrics, fullAnalysis }: TldrSummaryProps) {
  const [isOpen, setIsOpen] = useState(false)

  if (metrics.length === 0) {
    return null
  }

  // Split metrics into top row (volatility, average) and bottom row (peak, summary)
  const volatilityMetric = metrics.find(m => m.type === 'volatility')
  const averageMetric = metrics.find(m => m.type === 'average')
  const peakMetric = metrics.find(m => m.type === 'peak')
  const summaryMetric = metrics.find(m => m.type === 'summary')

  const renderMetric = (metric: MetricInsight | undefined, index: number) => {
    if (!metric) return null

    return (
      <motion.div
        key={metric.type}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: index * 0.1 }}
        className={`flex flex-col gap-2 p-3 sm:p-4 rounded-lg border ${getMetricBgColor(metric.type)}`}
      >
        <div className="flex items-center gap-2">
          {getMetricIcon(metric.type)}
          <span className="text-[10px] sm:text-xs font-medium text-muted-foreground tracking-wide">
            {metric.label}
          </span>
        </div>
        <div className="space-y-1">
          <p className="text-sm sm:text-base font-bold leading-tight">{metric.value}</p>
          {metric.context && (
            <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed">
              {metric.context}
            </p>
          )}
        </div>
      </motion.div>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="space-y-3">
        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {/* Top Row: Volatility, Average */}
          {renderMetric(volatilityMetric, 0)}
          {renderMetric(averageMetric, 1)}

          {/* Bottom Row: Peak, Summary */}
          {renderMetric(peakMetric, 2)}
          {renderMetric(summaryMetric, 3)}
        </div>

        {/* Expand/Collapse Trigger */}
        <CollapsibleTrigger asChild>
          <button
            className="flex items-center gap-1.5 text-xs sm:text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            aria-label={isOpen ? "Skrýt podrobnosti" : "Zobrazit podrobnosti"}
          >
            <span>{isOpen ? "Skrýt podrobnosti" : "Zobrazit podrobnosti"}</span>
            {isOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        </CollapsibleTrigger>

        {/* Full Analysis - Collapsible */}
        <CollapsibleContent>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="pt-2 border-t border-primary/10"
              >
                <p className="text-xs sm:text-sm leading-relaxed text-pretty whitespace-pre-wrap text-muted-foreground">
                  {fullAnalysis}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
